import clsx from "clsx";
import { type ReactNode, useId, useMemo } from "react";
import Select, {
	components,
	type FormatOptionLabelMeta,
	type GroupBase,
	type MenuProps,
	type SingleValue,
} from "react-select";
import { useFullscreenModalContainer } from "../../../../useFullscreenModalContainer";
import type { ReportErrorSelectOption } from "./reportError.types";

interface ReportErrorSelectProps<
	Option extends ReportErrorSelectOption<TValue>,
	TValue extends string | number = string,
	Group extends GroupBase<Option> = GroupBase<Option>,
> {
	label: string;
	fieldClassName?: string;
	variant?: "default" | "correctEvent";
	options: readonly (Option | Group)[];
	value: Option | null;
	onChange: (option: Option | null) => void;
	placeholder: string;
	required?: boolean;
	isDisabled?: boolean;
	isSearchable?: boolean;
	noOptionsMessage?: string;
	menuHeaderLabel?: string;
	formatOptionLabel?: (
		option: Option,
		meta: FormatOptionLabelMeta<Option>,
	) => ReactNode;
	menuPortalTarget?: HTMLElement | null;
	menuPosition?: "absolute" | "fixed";
	menuPlacement?: "auto" | "bottom" | "top";
}

export function ReportErrorSelect<
	Option extends ReportErrorSelectOption<TValue>,
	TValue extends string | number = string,
	Group extends GroupBase<Option> = GroupBase<Option>,
>({
	label,
	fieldClassName,
	variant = "default",
	options,
	value,
	onChange,
	placeholder,
	required = false,
	isDisabled = false,
	isSearchable = false,
	noOptionsMessage = "",
	menuHeaderLabel,
	formatOptionLabel,
	menuPortalTarget,
	menuPosition = "absolute",
	menuPlacement = "auto",
}: ReportErrorSelectProps<Option, TValue, Group>) {
	const inputId = useId();
	const fullscreenModalContainer = useFullscreenModalContainer();
	const resolvedMenuPortalTarget = useMemo(() => {
		if (menuPortalTarget !== undefined) {
			return menuPortalTarget;
		}

		if (typeof document === "undefined") {
			return undefined;
		}

		return fullscreenModalContainer instanceof HTMLElement
			? fullscreenModalContainer
			: document.body;
	}, [fullscreenModalContainer, menuPortalTarget]);
	const selectComponents =
		menuHeaderLabel && variant === "correctEvent"
			? {
					Menu: (props: MenuProps<Option, false, Group>) => (
						<components.Menu {...props}>
							<div
								className={clsx(
									"reportErrorSelect__menuHeader",
									`reportErrorSelect__menuHeader--${variant}`,
								)}
							>
								{menuHeaderLabel}
							</div>
							{props.children}
						</components.Menu>
					),
			  }
			: undefined;

	return (
		<div className={clsx("reportErrorModal__field", fieldClassName)}>
			<label className="reportErrorModal__fieldLabel" htmlFor={inputId}>
				{label}
				{required ? (
					<span className="reportErrorModal__required">*</span>
				) : null}
			</label>
			<Select<Option, false, Group>
				inputId={inputId}
				unstyled
				classNamePrefix="reportErrorSelect"
				options={options}
				value={value}
				onChange={(option: SingleValue<Option>) => onChange(option)}
				placeholder={placeholder}
				isDisabled={isDisabled}
				isSearchable={isSearchable}
				menuPortalTarget={resolvedMenuPortalTarget}
				menuPosition={menuPosition}
				menuPlacement={menuPlacement}
				menuShouldScrollIntoView={false}
				getOptionValue={(option) => String(option.value)}
				noOptionsMessage={() => noOptionsMessage}
				formatOptionLabel={formatOptionLabel}
				components={selectComponents}
				classNames={{
					control: (state) =>
						clsx(
							"reportErrorSelect__control",
							`reportErrorSelect__control--${variant}`,
							{
								"reportErrorSelect__control--is-focused": state.isFocused,
								"reportErrorSelect__control--is-disabled": state.isDisabled,
							},
						),
					valueContainer: () =>
						clsx(
							"reportErrorSelect__valueContainer",
							`reportErrorSelect__valueContainer--${variant}`,
						),
					placeholder: () => "reportErrorSelect__placeholder",
					singleValue: () => "reportErrorSelect__singleValue",
					input: () => "reportErrorSelect__input",
					menu: () =>
						clsx(
							"reportErrorSelect__menu",
							`reportErrorSelect__menu--${variant}`,
						),
					menuList: () =>
						clsx(
							"reportErrorSelect__menuList",
							`reportErrorSelect__menuList--${variant}`,
						),
					option: (state) =>
						clsx(
							"reportErrorSelect__option",
							`reportErrorSelect__option--${variant}`,
							{
								"reportErrorSelect__option--is-focused": state.isFocused,
								"reportErrorSelect__option--is-selected": state.isSelected,
							},
						),
					group: () =>
						clsx(
							"reportErrorSelect__group",
							`reportErrorSelect__group--${variant}`,
						),
					groupHeading: () =>
						clsx(
							"reportErrorSelect__groupHeading",
							`reportErrorSelect__groupHeading--${variant}`,
						),
					indicatorsContainer: () => "reportErrorSelect__indicators",
					dropdownIndicator: (state) =>
						clsx("reportErrorSelect__dropdownIndicator", {
							"reportErrorSelect__dropdownIndicator--is-focused":
								state.isFocused,
						}),
					indicatorSeparator: () => "reportErrorSelect__indicatorSeparator",
					noOptionsMessage: () => "reportErrorSelect__noOptionsMessage",
					menuPortal: () =>
						clsx(
							"reportErrorSelect__menuPortal",
							`reportErrorSelect__menuPortal--${variant}`,
						),
				}}
			/>
		</div>
	);
}
